import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCreditMemoPrint } from '../../lib/api';

/**
 * The printable Credit Memo (R2 Phase E).
 *
 * Renders EXCLUSIVELY from the frozen `payload` a posted memo carries, so a
 * later rename or price edit can never change paper already issued. The root
 * MUST carry `billing-doc`: the print stylesheet (index.css) hides `body *`
 * and whitelists only that class and two others, so without it the printer
 * gets a blank sheet. A voided memo still reprints, watermarked VOID.
 */

function money(v: string | null | undefined): string {
  const n = Number(v ?? 0);
  return (Number.isFinite(n) ? n : 0).toLocaleString('en-PH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function longDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' });
}

export function CreditMemoPrintPage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['credit-memo-print', id],
    queryFn: () => getCreditMemoPrint(id!),
    enabled: Boolean(id),
  });

  const payload = data?.payload ?? null;
  const isVoid = data?.status === 'void';

  useEffect(() => {
    // Only ever auto-print issued paper — a draft has no memo at all.
    if (payload) window.print();
  }, [payload]);

  if (isLoading) return <p className="p-8 text-sm text-muted-foreground">Loading…</p>;
  if (isError)
    return <p className="p-8 text-sm text-destructive">Could not load this credit memo.</p>;
  if (!payload) {
    return (
      <div className="mx-auto max-w-2xl p-8">
        <h1 className="text-lg font-semibold">Not issued yet</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This credit memo is still a draft, so there is nothing to print. Post it first — that is
          the moment its figures are frozen.
        </p>
      </div>
    );
  }

  return (
    <div className="billing-doc relative mx-auto max-w-3xl bg-white p-8 text-black">
      {isVoid ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 flex items-center justify-center"
        >
          <span className="-rotate-12 rounded-md border-4 border-red-600 px-10 py-2 text-6xl font-bold tracking-[0.3em] text-red-600 opacity-30">
            VOID
          </span>
        </div>
      ) : null}
      <header className="flex items-start justify-between border-b pb-4">
        <div>
          <h1 className="text-xl font-bold">{payload.title}</h1>
          <p className="mt-1 font-mono text-sm">{payload.docNo}</p>
        </div>
        <div className="text-right text-sm">
          <div className="font-semibold">{payload.company.name ?? '—'}</div>
          {payload.company.branchName ? <div>{payload.company.branchName}</div> : null}
          {payload.company.address ? <div>{payload.company.address}</div> : null}
          {payload.company.tin ? <div>TIN {payload.company.tin}</div> : null}
          {payload.company.phone ? <div>{payload.company.phone}</div> : null}
        </div>
      </header>

      <section className="grid grid-cols-2 gap-6 py-4 text-sm">
        <div>
          <div className="text-xs uppercase tracking-wide text-neutral-500">Credited to</div>
          <div className="font-semibold">{payload.customer.name ?? '—'}</div>
          {payload.customer.tradeName ? <div>{payload.customer.tradeName}</div> : null}
          {payload.customer.addressLines.map((line, i) => (
            <div key={i}>{line}</div>
          ))}
          {payload.customer.tin ? <div>TIN {payload.customer.tin}</div> : null}
        </div>
        <div className="text-right">
          <div>
            <span className="text-neutral-500">Date: </span>
            {longDate(payload.memoDate)}
          </div>
          {payload.againstDocNo ? (
            <div>
              <span className="text-neutral-500">Against: </span>
              {payload.againstDocNo}
            </div>
          ) : null}
          {payload.reference ? (
            <div>
              <span className="text-neutral-500">Reference: </span>
              {payload.reference}
            </div>
          ) : null}
          <div>
            <span className="text-neutral-500">Reason: </span>
            {payload.reason}
          </div>
        </div>
      </section>

      <table className="w-full text-sm">
        <thead className="border-y text-left text-xs uppercase tracking-wide text-neutral-500">
          <tr>
            <th className="py-2">Description</th>
            <th className="py-2 text-right">Qty</th>
            <th className="py-2 text-right">Unit price</th>
            <th className="py-2 text-right">Amount</th>
          </tr>
        </thead>
        <tbody>
          {payload.lines.map((l, i) => (
            <tr key={i} className="border-b">
              <td className="py-2">
                {l.name}
                {l.sku ? (
                  <span className="ml-2 font-mono text-xs text-neutral-500">{l.sku}</span>
                ) : null}
              </td>
              <td className="py-2 text-right tabular-nums">{l.qty}</td>
              <td className="py-2 text-right tabular-nums">{money(l.unitPrice)}</td>
              <td className="py-2 text-right tabular-nums">{money(l.lineTotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <section className="mt-4 flex justify-end">
        <dl className="w-64 space-y-1 text-sm">
          {Number(payload.totals.vat) > 0 ? (
            <>
              <div className="flex justify-between">
                <dt className="text-neutral-500">VAT-able</dt>
                <dd className="tabular-nums">{money(payload.totals.vatable)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-neutral-500">VAT</dt>
                <dd className="tabular-nums">{money(payload.totals.vat)}</dd>
              </div>
            </>
          ) : null}
          {Number(payload.totals.exempt) > 0 ? (
            <div className="flex justify-between">
              <dt className="text-neutral-500">VAT-exempt</dt>
              <dd className="tabular-nums">{money(payload.totals.exempt)}</dd>
            </div>
          ) : null}
          {Number(payload.totals.zeroRated) > 0 ? (
            <div className="flex justify-between">
              <dt className="text-neutral-500">Zero-rated</dt>
              <dd className="tabular-nums">{money(payload.totals.zeroRated)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between border-t pt-1 text-base font-semibold">
            <dt>Total credit</dt>
            <dd className="tabular-nums">{money(payload.totals.total)}</dd>
          </div>
        </dl>
      </section>

      {payload.memo ? <p className="mt-6 whitespace-pre-wrap text-sm">{payload.memo}</p> : null}
      {isVoid ? (
        <p className="mt-6 text-sm font-semibold text-red-700">
          VOID{data?.voidedAt ? ` since ${longDate(data.voidedAt)}` : ''}
          {data?.voidReason ? ` — ${data.voidReason}` : ''}. This credit cannot be used.
        </p>
      ) : null}
      <footer className="mt-8 border-t pt-3 text-xs text-neutral-500">{payload.footer}</footer>
    </div>
  );
}
